import React, { useState, useEffect } from 'react';
import { X, Search, Car, Plus, User, Phone } from 'lucide-react';
import { api } from '../../services/api';
import CustomerForm from '../forms/CustomerForm';
import VehicleForm from '../forms/VehicleForm';
import { COUNTRIES, getCountryByCode, getPopularCountries, searchCountries, Country } from '../../data/countryCodes';

// Type definitions
interface CustomerType {
  id: number;
  first_name: string;
  last_name: string;
  name?: string; // For backward compatibility
  country_code: string;
  phone_number_only: string;
  phone_number: string;
  email?: string;
  address?: string;
  language: string;
  is_active: boolean;
  created_at: string;
}

interface VehicleType {
  id: number;
  customer_id: number;
  make: string;
  model: string;
  year: number;
  color: string;
  plate_number: string;
  vin?: string;
  engine_number?: string;
  mileage: number;
  fuel_type: string;
  transmission_type: string;
  notes?: string;
}

interface CustomerCheckInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectVehicle: (customer: CustomerType, vehicle: VehicleType) => void;
}

const CustomerCheckInModal: React.FC<CustomerCheckInModalProps> = ({
  isOpen,
  onClose,
  onSelectVehicle,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [customer, setCustomer] = useState<CustomerType | null>(null);
  const [vehicles, setVehicles] = useState<VehicleType[]>([]);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [showNewVehicleForm, setShowNewVehicleForm] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Set default country to Kuwait
    const kuwait = getCountryByCode('KW');
    if (kuwait && !selectedCountry) {
      setSelectedCountry(kuwait);
    }
    
    if (!isOpen) {
      // Reset state when modal closes
      setPhoneNumber('');
      setCustomer(null);
      setVehicles([]);
      setShowNewCustomerForm(false);
      setShowNewVehicleForm(false);
      setError('');
      setCountrySearchTerm('');
    }
  }, [isOpen, selectedCountry]);

  const searchCustomer = async () => {
    if (!phoneNumber) {
      setError('Please enter a phone number');
      return;
    }

    setIsSearching(true);
    setError('');

    try {
      const response = await api.post('/customers/search-by-phone', {
        country_code: selectedCountry?.dialCode || '965',
        phone_number: phoneNumber
      });
      
      if (response.data.found) {
        setCustomer(response.data.customer);
        setVehicles(response.data.vehicles || []);
      } else {
        setError('Customer not found');
        setShowNewCustomerForm(true);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('Customer not found');
        setShowNewCustomerForm(true);
      } else {
        setError('Error searching for customer');
      }
    } finally {
      setIsSearching(false);
    }
  };


  const handleNewCustomerCreated = async (data: Partial<{ first_name: string; last_name: string; email?: string; phone: string; address: string; }>) => {
    try {
      // Create the customer with the API
      const response = await api.post('/customers', {
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email,
        phone_number: data.phone || `+${selectedCountry?.dialCode}${phoneNumber}`,
        address: data.address,
        country_code: selectedCountry?.dialCode || '965',
        phone_number_only: phoneNumber,
        language: 'en',
        role: 'customer'
      });
      
      if (response.data) {
        setCustomer(response.data);
        setShowNewCustomerForm(false);
        setVehicles([]);
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleNewVehicleCreated = async (data: Partial<any>) => {
    try {
      // Create the vehicle with the API
      const response = await api.post('/vehicles', {
        ...data,
        customer_id: customer?.id
      });
      
      if (response.data) {
        setVehicles([...vehicles, response.data]);
        setShowNewVehicleForm(false);
      }
    } catch (error) {
      console.error('Error creating vehicle:', error);
    }
  };

  const handleSelectVehicle = (vehicle: VehicleType) => {
    if (customer) {
      onSelectVehicle(customer, vehicle);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Customer Check-In</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Phone Search Section */}
        {!customer && !showNewCustomerForm && (
          <div className="space-y-4">
            <div className="flex space-x-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
                >
                  {selectedCountry ? (
                    <div className="flex items-center">
                      <span className="mr-1">{selectedCountry.flag}</span>
                      <span className="text-sm">+{selectedCountry.dialCode}</span>
                    </div>
                  ) : (
                    <span className="text-gray-500">Select Country</span>
                  )}
                </button>
                
                {showCountryDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-64 overflow-hidden">
                    <div className="p-2 border-b">
                      <input
                        type="text"
                        placeholder="Search countries..."
                        value={countrySearchTerm}
                        onChange={(e) => setCountrySearchTerm(e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="overflow-y-auto max-h-48">
                      {(countrySearchTerm ? searchCountries(countrySearchTerm) : getPopularCountries())
                        .filter(country => country.code !== 'IL')
                        .map((country) => (
                          <button
                            key={country.code}
                            onClick={() => {
                              setSelectedCountry(country);
                              setShowCountryDropdown(false);
                              setCountrySearchTerm('');
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between"
                          >
                            <div className="flex items-center">
                              <span className="mr-2">{country.flag}</span>
                              <span className="text-sm">{country.name}</span>
                            </div>
                            <span className="text-sm text-gray-500">+{country.dialCode}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1 relative">
                <input
                  type="tel"
                  placeholder="Enter phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                  onKeyPress={(e) => e.key === 'Enter' && searchCustomer()}
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              <button
                onClick={searchCustomer}
                disabled={isSearching}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isSearching ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
              </button>
            </div>
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
          </div>
        )}

        {/* New Customer Form */}
        {showNewCustomerForm && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-md font-medium text-gray-900">Create New Customer</h4>
              <button
                onClick={() => {
                  setShowNewCustomerForm(false);
                  setError('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
            <CustomerForm
              customer={{
                first_name: '',
                last_name: '',
                email: '',
                phone: `+${selectedCountry?.dialCode}${phoneNumber}`,
                address: ''
              }}
              onSubmit={handleNewCustomerCreated}
              onCancel={() => setShowNewCustomerForm(false)}
            />
          </div>
        )}

        {/* Customer Details */}
        {customer && !showNewVehicleForm && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{customer.first_name} {customer.last_name}</h4>
                  <p className="text-sm text-gray-600">+{customer.country_code} {customer.phone_number_only}</p>
                  {customer.email && (
                    <p className="text-sm text-gray-600">{customer.email}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-900">Vehicles</h4>
                <button
                  onClick={() => setShowNewVehicleForm(true)}
                  className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Vehicle</span>
                </button>
              </div>

              {vehicles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Car className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>No vehicles found</p>
                  <button
                    onClick={() => setShowNewVehicleForm(true)}
                    className="mt-2 text-blue-600 hover:text-blue-700"
                  >
                    Add first vehicle
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {vehicles.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <Car className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="font-medium">
                              {vehicle.make} {vehicle.model} ({vehicle.year})
                            </p>
                            <p className="text-sm text-gray-600">
                              {vehicle.plate_number} • {vehicle.vin || 'No VIN'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleSelectVehicle(vehicle)}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          Select & Continue
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setCustomer(null);
                setVehicles([]);
                setPhoneNumber('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Search for different customer
            </button>
          </div>
        )}

        {/* New Vehicle Form */}
        {showNewVehicleForm && customer && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-md font-medium text-gray-900">Add New Vehicle</h4>
              <button
                onClick={() => setShowNewVehicleForm(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
            <VehicleForm
              vehicle={{
                customer_id: customer.id,
                make: '',
                model: '',
                year: new Date().getFullYear(),
                color: '',
                plate_number: '',
                vin: '',
                engine_number: '',
                mileage: 0,
                fuel_type: 'Petrol',
                transmission_type: 'Automatic',
                notes: ''
              }}
              onSubmit={handleNewVehicleCreated}
              onCancel={() => setShowNewVehicleForm(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerCheckInModal;